// 2026-06-20 (user request) — Dialog admin "Tentukan Kategori WO" (bulk).
// Aturan: hanya WO yang BELUM DIKLASIFIKASIKAN (is_urgent kosong/null/field
// hilang) yang diproses; WO yang sudah terklasifikasi ditampilkan sebagai
// "Dilewati" dan TIDAK ditimpa. Preview wajib tampil sebelum simpan; simpan
// hanya setelah admin memilih kategori + klik konfirmasi. Backend mencatat
// audit log per WO (kategori lama → baru, waktu, pengguna).
import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { Tag, X } from "lucide-react";

const isClassified = (w) => w?.is_urgent === true || w?.is_urgent === false;

export default function WoCategoryBulkDialog({ open, onOpenChange, wos = [], onDone }) {
  const [category, setCategory] = useState(null); // "urgent" | "non_urgent" | null
  const [busy, setBusy] = useState(false);

  const eligible = useMemo(() => wos.filter((w) => !isClassified(w)), [wos]);
  const skipped = useMemo(() => wos.filter((w) => isClassified(w)), [wos]);
  const newLabel = category === "urgent" ? "Urgent Case" : category === "non_urgent" ? "Non-Urgent Case" : "—";

  const close = () => { setCategory(null); onOpenChange(false); };

  const confirm = async () => {
    if (!category || eligible.length === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/wo-category/bulk", {
        wo_ids: eligible.map((w) => w.id),
        category,
      });
      toast.success(
        `${data.updated} WO diklasifikasikan sebagai ${newLabel}` +
        ((data.skipped || []).length > 0 ? ` · ${data.skipped.length} dilewati` : "") +
        " — tercatat di audit log"
      );
      setCategory(null);
      if (typeof onDone === "function") onDone();
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail) || "Gagal menyimpan kategori");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto rounded-none" data-testid="wo-cat-bulk-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tag size={15} className="text-[#0B1D3A]" /> Tentukan Kategori WO
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Mengisi kategori untuk WO yang <b>Belum Diklasifikasikan</b>. Hanya field kategori yang diubah — milestone, status, tanggal, nilai, dan invoice tidak tersentuh. Setiap perubahan dicatat di audit log.
          </DialogDescription>
        </DialogHeader>

        {/* Pilihan kategori */}
        <div className="flex gap-2" data-testid="wo-cat-options">
          {[
            { v: "urgent", label: "Urgent Case", cls: category === "urgent" ? "bg-rose-700 border-rose-700 text-white" : "bg-white border-slate-300 text-slate-600 hover:bg-rose-50" },
            { v: "non_urgent", label: "Non-Urgent Case", cls: category === "non_urgent" ? "bg-[#0B1D3A] border-[#0B1D3A] text-white" : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50" },
          ].map((o) => (
            <button key={o.v} type="button" data-testid={`wo-cat-opt-${o.v === "non_urgent" ? "non-urgent" : o.v}`} onClick={() => setCategory(o.v)}
              className={`flex-1 px-3 py-2.5 border text-[11px] uppercase tracking-wider font-bold transition-colors ${o.cls}`}>
              {o.label}
            </button>
          ))}
        </div>

        {/* Preview */}
        <div className="border border-slate-200" data-testid="wo-cat-preview">
          <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-600">
            Preview — {eligible.length} WO akan diubah: Belum Diklasifikasikan → {newLabel}
          </div>
          {eligible.length === 0 ? (
            <div className="p-4 text-center text-[11px] text-slate-400 italic" data-testid="wo-cat-empty">
              Tidak ada WO terpilih yang belum diklasifikasikan.
            </div>
          ) : (
            <table className="w-full text-[11px]">
              <thead className="border-b border-slate-100">
                <tr className="text-left">{["WO", "Judul", "Outlet", "Kategori Lama", "Kategori Baru"].map((h) => <th key={h} className="px-3 py-1.5 overline text-slate-500">{h}</th>)}</tr>
              </thead>
              <tbody>
                {eligible.map((w) => (
                  <tr key={w.id} className="border-b border-slate-50" data-testid={`wo-cat-row-${w.id}`}>
                    <td className="px-3 py-1.5 font-mono font-semibold text-[#0B1D3A]">{w.wo_number || `#${w.id.slice(0, 6)}`}</td>
                    <td className="px-3 py-1.5 text-slate-600 max-w-[180px] truncate" title={w.wo_title}>{w.wo_title || "—"}</td>
                    <td className="px-3 py-1.5 text-slate-600 max-w-[140px] truncate">{(w.parent_project_name || w.project_name || "—").trim()}</td>
                    <td className="px-3 py-1.5"><span className="inline-block px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider border bg-amber-50 border-amber-300 text-amber-800">Belum Diklasifikasikan</span></td>
                    <td className="px-3 py-1.5">
                      {category ? (
                        <span className={`inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border ${category === "urgent" ? "bg-rose-100 border-rose-400 text-rose-800" : "bg-slate-100 border-slate-400 text-slate-700"}`}>{newLabel}</span>
                      ) : <span className="text-slate-300 italic text-[10px]">pilih kategori dulu</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Dilewati */}
        {skipped.length > 0 && (
          <div className="border border-slate-200" data-testid="wo-cat-skipped">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Dilewati — {skipped.length} WO sudah terklasifikasi (tidak ditimpa)
            </div>
            <div className="px-3 py-2 space-y-1">
              {skipped.map((w) => (
                <div key={w.id} className="text-[11px] text-slate-500" data-testid={`wo-cat-skip-${w.id}`}>
                  <span className="font-mono font-semibold">{w.wo_number || `#${w.id.slice(0, 6)}`}</span> — sudah {w.is_urgent === true ? "Urgent Case" : "Non-Urgent Case"}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={close} className="rounded-none" data-testid="wo-cat-cancel">
            <X size={12} className="mr-1" /> Batal
          </Button>
          <Button
            onClick={confirm}
            disabled={busy || !category || eligible.length === 0}
            className="rounded-none bg-[#FF5A1F] hover:bg-[#e04a13] text-white"
            data-testid="wo-cat-confirm"
          >
            {busy ? "Menyimpan..." : `Simpan Kategori (${eligible.length} WO)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
